import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@components/Layout';
import useIsAuthenticated from '@core/hooks/useIsAuthenticated';
import useProfile from '@core/hooks/useProfile';
import useStudyInfo from '@core/hooks/useStudyInfo';
import ThemeProvider from '@core/providers/ThemeProvider';
import { RoleName, ViewerInfoCommand } from '@core/types';
import { hideStartLoader } from '@core/utils/startLoader';
import { ReportsSource, useReportsViewState } from 'app/features/reports/view';
import InfoManager from 'app/features/study/components/InfoManager';
import Viewer, { CommandArguments } from 'app/features/study/components/Viewer';
import StudyConfirmAuthWrapper from '../components/StudyConfirmAuthWrapper';

const ReadOnlyViewer = () => {
  const [command, setCommand] = useState<ViewerInfoCommand | undefined>();
  const [searchParams, setSearchParams] = useSearchParams();
  const studyId = searchParams.get('s') ?? '';
  const isAuthenticated = useIsAuthenticated();
  const { studyInfo } = useStudyInfo({
    studyInstanceUidOrHash: studyId,
    isSharedStudy: true,
  });
  const navigate = useNavigate();
  const { role } = useProfile();
  const reportsView = useReportsViewState();
  const reportsSource = useMemo<ReportsSource>(
    () => ({ kind: 'shared', studyHash: studyId }),
    [studyId],
  );

  const handleLoginClick = () => {
    sessionStorage.setItem('sessionType', 'study');
    navigate('/redirect');
  };

  useEffect(() => {
    if (studyInfo) {
      const studyIds = {
        studyInstanceUid: studyInfo.studyInstanceUid,
        studyHash: studyId,
      };
      sessionStorage.setItem('studyIds', JSON.stringify(studyIds));
    }
  }, [studyInfo]);

  // A link that opens the reports (`?report=true`) shows them without waiting
  // for the images viewer.
  useEffect(() => {
    if (reportsView.isOpen) hideStartLoader({});
  }, [reportsView.isOpen]);

  function removeFilter(name: string) {
    const existing: Record<string, string> = {};

    for (const [key, param] of searchParams.entries()) {
      if (key === name) {
        continue;
      }

      existing[key] = param;
    }

    setSearchParams({ ...existing });
  }

  const onInfoManagerClose = () => {
    setCommand(undefined);
    removeFilter('section');
  };

  // Stable: the viewer is reinstalled when its command handler changes.
  const handleCommandClick = useCallback(
    ({ commandName }: CommandArguments) => setCommand(commandName),
    [],
  );

  return (
    <MainLayout onLogin={handleLoginClick}>
      <ThemeProvider orgId={studyInfo?.organizationId ?? ''} studyHash={studyId ?? ''}>
        <StudyConfirmAuthWrapper studyInfo={studyInfo}>
          <Viewer
            studyInstanceUid={studyInfo?.studyInstanceUid ?? ''}
            baseUrl={`/study/view`}
            isAnon={!isAuthenticated}
            isAdministrator={false}
            readOnly={!isAuthenticated || role === RoleName.Patient}
            onCommandClick={handleCommandClick}
            reports={{ source: reportsSource, view: reportsView, variant: 'shared' }}
          />
          <InfoManager
            studyInstanceUid={studyId}
            studyInfo={studyInfo}
            readOnly
            command={command}
            onClose={onInfoManagerClose}
          />
        </StudyConfirmAuthWrapper>
      </ThemeProvider>
    </MainLayout>
  );
};

export default ReadOnlyViewer;
